"use client";

import * as React from "react";
import { message } from "antd";
import { IChatMessage, IStatus, LogEvent, IChatMessageWithSession } from "@/types/chat-types";
import MessageList from "./messagelist";
import ChatInput from "./chatinput";

export interface ChatViewProps {
  initMessages: IChatMessage[];
  viewHeight?: string;
  onSendMessage: (message?: string) => Promise<void>;
  isLoading: boolean;
  onStopGeneration?: () => void;
  userInput?: any;
  setUserInput?: (userInput: any) => void;
}


export default function ChatView({
  initMessages,
  onSendMessage,
  isLoading,
  onStopGeneration,
  userInput,
  setUserInput,
  viewHeight = "100vh"
}: ChatViewProps) {
  const [messages, setMessages] = React.useState<IChatMessage[]>(initMessages);
  const [error, setError] = React.useState<IStatus | null>(null);
  const chatContainerRef = React.useRef<HTMLDivElement>(null);

  // Update messages when initMessages changes
  React.useEffect(() => {
    setMessages(initMessages);
  }, [initMessages]);

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Add this near the top with other hooks
  const hasSentInitialMessage = React.useRef(false);

  // Replace the existing useEffect with this:
  React.useEffect(() => {
    const sendInitialGreeting = async () => {
      if (messages.length === 0 && !hasSentInitialMessage.current) {
        hasSentInitialMessage.current = true;
        await handleSendMessage("Start the execution");
      }
    };
    
    sendInitialGreeting();
  }, []);

  const handleSendMessage = async (message?: string) => {
    if (!message?.trim()) return;
    
    try {
      setError(null);
      await onSendMessage(message);
    } catch (err) {
      console.error('Error sending message:', err);
      setError({
        status: false,
        message: err instanceof Error ? err.message : 'Failed to send message',
      });
    }
  };

  const getBaseUrl = (url: string): string => {
    try {
      // Remove protocol and api path for WebSocket
      return url
        .replace(/(^\w+:|^)\/\//, "") // Remove protocol (http:// or https://)
        .replace("/api", ""); // Remove /api for base
    } catch (error) {
      console.error("Error processing server URL:", error);
      throw new Error("Invalid server URL configuration");
    }
  };

  // Process messages to include input prompts
  // const processedMessages = React.useMemo(() => {
  //   if (userInput.input_type === 'TEXT') {
  //       return [...messages, {
  //         id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  //         text: userInput.question,
  //         sender: 'user',
  //         timestamp: new Date().toISOString(),
  //       }];
  //   }
  //   return messages;
  // }, [messages, userInput]);

  return (
    <div className="flex flex-col h-full">
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4"
        style={{ height: `calc(${viewHeight} - 100px)` }}
      >
        <MessageList
          messages={messages}
          sessionLogs={{}}
          onRetry={handleSendMessage}
          onSubmit={handleSendMessage}
          loading={isLoading}
          userInput={userInput}
          setUserInput={setUserInput}
        />
      </div>
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <ChatInput
          onSubmit={handleSendMessage}
          loading={isLoading}
          error={error}
          onStopGeneration={onStopGeneration}
        />
      </div>
    </div>
  );
}